/**
 * ChipMultiSelect — sélection multi-valeur sous forme de chips toggle.
 *
 *   <ChipMultiSelect
 *     options={EMOTIONAL_INTENT_OPTIONS.map(o => ({ value: o, label: EMOTIONAL_INTENT_FR[o] }))}
 *     value={value}
 *     onChange={onChange}
 *     max={3}
 *   />
 *
 * Usage typique : Tier 2 du formulaire NewWorkPage — `emotional_intent`
 * (2-3 max) et `target_audience` (1-3) sont des slugs neutres EN
 * affichés en français via lookup.
 *
 * Pas de label intégré : wrapper avec `<Field>` côté appelant.
 */

import { Check } from 'lucide-react'

import './ChipMultiSelect.css'

export interface ChipOption<TValue extends string = string> {
  value: TValue
  label: string
}

interface ChipMultiSelectProps<TValue extends string> {
  options: ChipOption<TValue>[]
  value: TValue[]
  onChange: (next: TValue[]) => void
  /** Limite max de selections. Au-dela, les autres chips deviennent disabled. */
  max?: number
  ariaLabel?: string
  className?: string
  id?: string
  'aria-describedby'?: string
}

export default function ChipMultiSelect<TValue extends string>({
  options,
  value,
  onChange,
  max,
  ariaLabel,
  className,
  id,
  ...rest
}: ChipMultiSelectProps<TValue>) {
  const selected = new Set(value)
  const reachedMax = max !== undefined && value.length >= max

  const toggle = (next: TValue) => {
    if (selected.has(next)) {
      onChange(value.filter((v) => v !== next))
      return
    }
    if (reachedMax) return
    onChange([...value, next])
  }

  const classes = ['chip-multi', className].filter(Boolean).join(' ')

  return (
    <div role="group" aria-label={ariaLabel} id={id} className={classes} {...rest}>
      {options.map((opt) => {
        const active = selected.has(opt.value)
        const disabled = !active && reachedMax
        const chipClasses = ['chip-multi-chip', active && 'chip-multi-chip--active']
          .filter(Boolean)
          .join(' ')
        return (
          <button
            key={opt.value}
            type="button"
            role="checkbox"
            aria-checked={active}
            aria-disabled={disabled}
            disabled={disabled}
            className={chipClasses}
            onClick={() => toggle(opt.value)}
          >
            {active && <Check size={12} aria-hidden="true" />}
            <span>{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
